import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, X, CheckCircle, Plus, Search, ShoppingCart, Trash2, ChevronDown, ChevronRight, FileText, FileUp } from 'lucide-react';
import Layout from '../components/Layout';
import settlementService from '../services/settlementService';
import purchaseService from '../services/purchaseService';
import salesService from '../services/salesService';
import authService from '../services/authService';

const SettlementFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const mode = id ? 'edit' : 'create';
  const user = authService.getCurrentUser();
  const canViewPrices = user && ['admin', 'accounting'].includes(user.role);

  const [loading, setLoading] = useState(mode === 'edit' && !location.state?.settlementData);
  const [error, setError] = useState('');
  const [settlement, setSettlement] = useState(location.state?.settlementData || null);
  const [notification, setNotification] = useState({ open: false, severity: 'success', message: '' });
  
  // Form state
  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    settlement_date: new Date().toISOString().split('T')[0], // Default to today
    purchase_orders: [],
    subtotal: 0,
    tax_rate: 0.13,
    tax_amount: 0,
    discount_amount: 0,
    total_amount: 0,
    comment: '',
    price_includes_tax: true,
    amount_variance_reason: '',
    quantity_variance_reason: ''
  });


  // PO Selection dialog
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [availablePOs, setAvailablePOs] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [poSearchTerm, setPoSearchTerm] = useState('');
  
  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Price toggle state
  const [priceIncludesTax, setPriceIncludesTax] = useState(true);
  
  // Expanded POs state
  const [expandedPOs, setExpandedPOs] = useState(new Set());
  
  // Drawer state for PO/SO details
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null); // 'po' or 'so'
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Load settlement for edit mode
  const loadSettlement = useCallback(async () => {
    if (mode !== 'edit' || !id || settlement) return;
    try {
      setLoading(true);
      const resp = await settlementService.getSettlement(id);
      setSettlement(resp);

      // Derive price-includes-tax flag from either explicit field or pricing_mode
      const derivedPriceIncludesTax =
        resp.price_includes_tax ??
        (resp.pricing_mode === 'tax_inclusive' ? true :
         resp.pricing_mode === 'tax_exclusive' ? false : true);

      // Format ISO date/datetime to YYYY-MM-DD for date input
      const rawDate = resp.settlement_date;
      let settlementDate = new Date().toISOString().split('T')[0];
      if (rawDate) {
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) {
          settlementDate = d.toISOString().split('T')[0];
        }
      }

      setFormData({
        supplier_id: resp.supplier_id || '',
        supplier_name: resp.supplier_name || '',
        settlement_date: settlementDate,
        purchase_orders: resp.purchase_orders || [],
        subtotal: resp.subtotal || resp.subtotal_tax_inclusive || resp.subtotal_tax_exclusive || 0,
        tax_rate: resp.tax_rate || 0.13,
        tax_amount: resp.tax_amount || 0,
        discount_amount: resp.discount_amount || 0,
        total_amount: resp.total_amount || 0,
        comment: resp.notes || resp.comment || '',
        price_includes_tax: derivedPriceIncludesTax,
        amount_variance_reason: resp.amount_variance_reason || '',
        quantity_variance_reason: resp.quantity_variance_reason || ''
      });
      setPriceIncludesTax(derivedPriceIncludesTax);
    } catch (err) {
      setError(err?.response?.data?.error || '加载结算单失败');
    } finally {
      setLoading(false);
    }
  }, [id, mode, settlement]);

  useEffect(() => {
    loadSettlement();
  }, [loadSettlement]);

  const handleClose = () => {
    if (mode === 'edit') {
      navigate(`/settlements/${id}/view`);
    } else {
      navigate('/settlements');
    }
  };

  // Load available POs for selection
  const loadAvailablePOs = useCallback(async () => {
    try {
      setPoLoading(true);
      const response = await settlementService.getAvailablePOs({
        status: 'confirmed',
        supplier_id: formData.supplier_id || undefined
      });
      const pos = response.purchase_orders || response || [];
      
      // Fetch SO mappings for each PO
      const posWithSO = await Promise.all(
        pos.map(async (po) => {
          try {
            const soInfo = await fetchSOForPO(po._id);
            return { ...po, linked_so: soInfo };
          } catch (e) {
            return { ...po, linked_so: null };
          }
        })
      );
      
      setAvailablePOs(posWithSO);
    } catch (err) {
      console.error('Error loading available POs:', err);
      setAvailablePOs([]);
    } finally {
      setPoLoading(false);
    }
  }, [formData.supplier_id]);

  // Fetch SO info for a PO
  const fetchSOForPO = async (poId) => {
    try {
      // Try to get SO mapping from API
      const response = await purchaseService.getPurchaseOrder(poId);
      if (response && response.source_sales_order) {
        return {
          so_id: response.source_sales_order.sales_order_id || response.source_sales_order._id,
          so_number: response.source_sales_order.sales_order_id || response.source_sales_order.order_number
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const handleOpenPoDialog = () => {
    loadAvailablePOs();
    setPoDialogOpen(true);
  };

  const handleSelectPO = async (po) => {
    // Check if PO is already selected
    if (formData.purchase_orders.some(existing => existing._id === po._id)) {
      setNotification({ open: true, severity: 'warning', message: '该采购单已添加' });
      return;
    }

    // Validate supplier consistency
    if (formData.supplier_id && formData.supplier_id !== po.supplier_id) {
      setNotification({ 
        open: true, 
        severity: 'error', 
        message: '供应商不一致：只能添加同一供应商的采购单' 
      });
      return;
    }

    // Fetch full PO details including products
    try {
      const poDetails = await purchaseService.getPurchaseOrder(po._id);
      
      // Fetch SO info
      const soInfo = await fetchSOForPO(po._id);
      
      // Build product items with editable fields
      const products = (poDetails.products || []).map(product => ({
        product_id: product.product_id || product._id || product.product_code,
        product_code: product.product_code || product.product_id,
        product_name: product.name || product.product_name,
        specification: product.specification || '',
        unit: product.unit || '',
        ordered_qty: product.quantity || 0,
        received_qty: product.received_quantity || product.quantity_received || 0,
        settlement_qty: product.received_quantity || product.quantity_received || 0,
        unit_price: product.unit_price || product.price || 0,
        subtotal: (product.received_quantity || product.quantity_received || 0) * (product.unit_price || product.price || 0)
      }));

      // Add PO to form
      const newPO = {
        _id: po._id,
        purchase_order_id: po.purchase_order_id,
        supplier_id: po.supplier_id,
        supplier_name: po.supplier_name,
        total_amount: po.total_cost || po.total_amount || 0,
        received_qty: po.received_qty || 0,
        settlement_qty: po.received_qty || 0,
        status: po.status,
        linked_so: soInfo,
        products: products,
        po_total_amount: po.total_cost || po.total_amount || 0
      };

      const updatedPOs = [...formData.purchase_orders, newPO];
      setFormData(prev => ({
        ...prev,
        supplier_id: po.supplier_id,
        supplier_name: po.supplier_name,
        purchase_orders: updatedPOs
      }));

      // Expand the newly added PO
      setExpandedPOs(prev => new Set([...prev, po._id]));

      // Recalculate amounts
      calculateTotals(updatedPOs, formData.tax_rate, formData.discount_amount, priceIncludesTax);
      setPoDialogOpen(false);
    } catch (err) {
      setNotification({ open: true, severity: 'error', message: '获取采购单详情失败' });
    }
  };

  const handleRemovePO = (poId) => {
    const updatedPOs = formData.purchase_orders.filter(po => po._id !== poId);
    setFormData(prev => ({
      ...prev,
      purchase_orders: updatedPOs
    }));
    calculateTotals(updatedPOs, formData.tax_rate, formData.discount_amount, priceIncludesTax);
  };

  const togglePOExpand = (poId) => {
    setExpandedPOs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(poId)) {
        newSet.delete(poId);
      } else {
        newSet.add(poId);
      }
      return newSet;
    });
  };

  const handleProductQtyChange = (poId, productIndex, newQty) => {
    const updatedPOs = formData.purchase_orders.map(po => {
      if (po._id !== poId) return po;
      
      const updatedProducts = po.products.map((product, idx) => {
        if (idx !== productIndex) return product;
        return {
          ...product,
          settlement_qty: parseFloat(newQty) || 0,
          subtotal: (parseFloat(newQty) || 0) * (product.unit_price || 0)
        };
      });
      
      return {
        ...po,
        products: updatedProducts,
        settlement_qty: updatedProducts.reduce((sum, p) => sum + (p.settlement_qty || 0), 0)
      };
    });
    
    setFormData(prev => ({
      ...prev,
      purchase_orders: updatedPOs
    }));
    calculateTotals(updatedPOs, formData.tax_rate, formData.discount_amount, priceIncludesTax);
  };

  const handleProductPriceChange = (poId, productIndex, newPrice) => {
    const updatedPOs = formData.purchase_orders.map(po => {
      if (po._id !== poId) return po;
      
      const updatedProducts = po.products.map((product, idx) => {
        if (idx !== productIndex) return product;
        return {
          ...product,
          unit_price: parseFloat(newPrice) || 0,
          subtotal: (product.settlement_qty || 0) * (parseFloat(newPrice) || 0)
        };
      });
      
      return {
        ...po,
        products: updatedProducts
      };
    });
    
    setFormData(prev => ({
      ...prev,
      purchase_orders: updatedPOs
    }));
    calculateTotals(updatedPOs, formData.tax_rate, formData.discount_amount, priceIncludesTax);
  };

  const calculateTotals = (purchaseOrders, taxRate, discountAmount, includesTax) => {
    // Calculate based on product subtotals
    const subtotal = purchaseOrders.reduce((sum, po) => {
      return sum + po.products.reduce((poSum, product) => poSum + (product.subtotal || 0), 0);
    }, 0);
    
    const taxAmount = includesTax ? 0 : subtotal * taxRate;
    const totalAmount = subtotal + taxAmount - discountAmount;

    setFormData(prev => ({
      ...prev,
      subtotal,
      tax_amount: taxAmount,
      total_amount: Math.round(totalAmount * 100) / 100
    }));
  };

  const handleTaxRateChange = (value) => {
    const taxRate = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, tax_rate: taxRate }));
    calculateTotals(formData.purchase_orders, taxRate, formData.discount_amount, priceIncludesTax);
  };

  const handleDiscountChange = (value) => {
    const discountAmount = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, discount_amount: discountAmount }));
    calculateTotals(formData.purchase_orders, formData.tax_rate, discountAmount, priceIncludesTax);
  };

  const handlePriceIncludesTaxChange = (checked) => {
    setPriceIncludesTax(checked);
    calculateTotals(formData.purchase_orders, formData.tax_rate, formData.discount_amount, checked);
  };

  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.supplier_id) {
        throw new Error('请选择供应商');
      }
      if (formData.purchase_orders.length === 0) {
        throw new Error('请至少添加一个采购单');
      }
      
      // Validate settlement date
      if (!formData.settlement_date) {
        throw new Error('请选择结算日期');
      }
      const today = new Date().toISOString().split('T')[0];
      if (formData.settlement_date > today) {
        throw new Error('结算日期不能晚于今天');
      }
      
      // Check if settlement date >= last receipt date
      const lastReceiptDates = formData.purchase_orders.map(po => {
        const productDates = po.products?.map(p => p.last_receipt_date).filter(Boolean) || [];
        return productDates.length > 0 ? new Date(Math.max(...productDates.map(d => new Date(d)))) : null;
      }).filter(Boolean);
      
      if (lastReceiptDates.length > 0) {
        const maxReceiptDate = new Date(Math.max(...lastReceiptDates));
        const settlementDate = new Date(formData.settlement_date);
        // Add one day to account for timezone differences
        maxReceiptDate.setHours(0, 0, 0, 0);
        settlementDate.setHours(0, 0, 0, 0);
        if (settlementDate < maxReceiptDate) {
          throw new Error(`结算日期不能早于最后入库日期 (${maxReceiptDate.toLocaleDateString('zh-CN')})`);
        }
      }
      
      if (!formData.supplier_id) {
        throw new Error('请选择供应商');
      }
      if (formData.purchase_orders.length === 0) {
        throw new Error('请至少添加一个采购单');
      }
      
      const submitData = {
        ...formData,
        price_includes_tax: priceIncludesTax,
        pricing_mode: priceIncludesTax ? 'tax_inclusive' : 'tax_exclusive',
        notes: formData.comment || '',
        po_total_amount: poTotalAmount,
        amount_variance: formData.total_amount - poTotalAmount
      };

      if (mode === 'edit') {
        await settlementService.updateSettlement(id, submitData);
        setNotification({ open: true, severity: 'success', message: '结算单已更新' });
        navigate(`/settlements/${id}/view`);
      } else {
        const created = await settlementService.createSettlement(submitData);
        setNotification({ open: true, severity: 'success', message: '结算单已创建' });
        const newId = created?.settlement?._id || created?._id;
        if (newId) {
          navigate(`/settlements/${newId}/view`);
        } else {
          navigate('/settlements');
        }
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || '保存结算单失败');
    }
  };

  // Open PO/SO drawer
  const openDrawer = async (type, id) => {
    setDrawerType(type);
    setDrawerOpen(true);
    setDrawerLoading(true);
    
    try {
      if (type === 'po') {
        const data = await purchaseService.getPurchaseOrder(id);
        setDrawerData(data);
      } else if (type === 'so') {
        const data = await salesService.getSalesOrder(id);
        setDrawerData(data);
      }
    } catch (err) {
      console.error('Error loading order details:', err);
    } finally {
      setDrawerLoading(false);
    }
  };

  // Auto-hide notification after 2.5 seconds
  useEffect(() => {
    if (notification.open) {
      const timer = setTimeout(() => {
        setNotification(prev => ({ ...prev, open: false }));
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [notification.open]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  // Calculate variance
  const poTotalAmount = formData.purchase_orders.reduce((sum, po) => sum + (po.po_total_amount || 0), 0);
  const amountVariance = formData.total_amount - poTotalAmount;
  const hasVariance = Math.abs(amountVariance) > 0.01;

  if (loading) {
    return (
      <Layout title={mode === 'edit' ? '编辑结算单' : '新建结算单'} activeTab="settlements">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={mode === 'edit' ? '编辑结算单' : '新建结算单'} activeTab="settlements">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Toast Notification */}
        {notification.open && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
            <Alert className={
              notification.severity === 'success'
                ? 'bg-success text-success-foreground border-success shadow-lg'
                : notification.severity === 'error'
                ? 'bg-destructive text-destructive-foreground border-destructive shadow-lg'
                : 'bg-warning text-warning-foreground border-warning shadow-lg'
            }>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-4">
                {notification.message}
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setNotification(prev => ({ ...prev, open: false }))}>
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        <Card>
          <CardContent className="p-6">
            {/* Basic Info Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">基本信息</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>供应商</Label>
                  <Input 
                    value={formData.supplier_name} 
                    readOnly 
                    className="bg-muted"
                    placeholder="选择采购单后将自动填充"
                  />
                </div>
                <div>
                  <Label>结算单编号</Label>
                  <Input 
                    value={mode === 'edit' ? settlement?.settlement_id : '自动生成'} 
                    readOnly 
                    className="bg-muted"
                  />
                </div>
                <div>
                  <Label htmlFor="settlement_date">结算日期 *</Label>
                  <Input
                    id="settlement_date"
                    type="date"
                    value={formData.settlement_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, settlement_date: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>
            </div>


            <Separator className="my-6" />

            {/* PO Selection Section */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">关联采购订单</h3>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setImportDialogOpen(true)}
                    variant="outline"
                    disabled={formData.purchase_orders.length > 0}
                    title="从文件导入功能开发中"
                  >
                    <FileUp className="h-4 w-4 mr-2" />
                    从文件导入
                  </Button>
                  <Button 
                    onClick={handleOpenPoDialog}
                    variant="outline"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    添加采购单
                  </Button>

                </div>
              </div>



              {formData.purchase_orders.length === 0 ? (
                <div className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>暂无采购单</p>
                  <p className="text-sm">点击"添加采购单"按钮选择采购单</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.purchase_orders.map((po) => (
                    <Card key={po._id} className="overflow-hidden">
                      {/* PO Header */}
                      <div 
                        className="bg-muted/50 p-4 flex items-center justify-between cursor-pointer hover:bg-muted"
                        onClick={() => togglePOExpand(po._id)}
                      >
                        <div className="flex items-center gap-4">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            {expandedPOs.has(po._id) ? 
                              <ChevronDown className="h-4 w-4" /> : 
                              <ChevronRight className="h-4 w-4" />
                            }
                          </Button>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              <span 
                                className="text-primary cursor-pointer hover:underline"
                                onClick={(e) => { e.stopPropagation(); openDrawer('po', po._id); }}
                              >
                                {po.purchase_order_id}
                              </span>
                              {po.linked_so && (
                                <Badge variant="outline" className="text-xs bg-blue-50">
                                  <FileText className="h-3 w-3 mr-1" />
                                  <span 
                                    className="cursor-pointer hover:underline"
                                    onClick={(e) => { e.stopPropagation(); openDrawer('so', po.linked_so.so_id); }}
                                  >
                                    {po.linked_so.so_number}
                                  </span>
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">{po.supplier_name}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">PO金额: {formatCurrency(po.po_total_amount)}</div>
                            <div className="text-sm text-muted-foreground">入库: {po.received_qty}</div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleRemovePO(po._id); }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* PO Products Detail */}
                      {expandedPOs.has(po._id) && (
                        <div className="p-4 border-t">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-muted-foreground border-b">
                                <th className="text-left py-2 px-2">产品</th>
                                <th className="text-right py-2 px-2">规格</th>
                                <th className="text-right py-2 px-2">单位</th>
                                <th className="text-right py-2 px-2">订购数量</th>
                                <th className="text-right py-2 px-2">实际收货数量</th>
                                <th className="text-right py-2 px-2">结算数量</th>
                                {canViewPrices && <th className="text-right py-2 px-2">单价</th>}
                                {canViewPrices && <th className="text-right py-2 px-2">小计</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {po.products?.map((product, idx) => (
                                <tr key={idx} className="border-b last:border-0">
                                  <td className="py-2 px-2">
                                    <div className="font-medium">{product.product_name}</div>
                                    <div className="text-xs text-muted-foreground">{product.product_code}</div>
                                  </td>
                                  <td className="text-right py-2 px-2 text-muted-foreground">{product.specification || '-'}</td>
                                  <td className="text-right py-2 px-2 text-muted-foreground">{product.unit || '-'}</td>
                                  <td className="text-right py-2 px-2">{product.ordered_qty}</td>
                                  <td className="text-right py-2 px-2">{product.received_qty}</td>
                                  <td className="text-right py-2 px-2">
                                    <Input
                                      type="number"
                                      min="0"
                                      max={product.received_qty}
                                      value={product.settlement_qty}
                                      onChange={(e) => handleProductQtyChange(po._id, idx, e.target.value)}
                                      className="w-20 h-8 text-right"
                                    />
                                  </td>
                                  {canViewPrices && (
                                  <td className="text-right py-2 px-2">
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={product.unit_price}
                                      onChange={(e) => handleProductPriceChange(po._id, idx, e.target.value)}
                                      className="w-24 h-8 text-right"
                                      placeholder="输入单价"
                                    />
                                  </td>
                                  )}
                                  {canViewPrices && (
                                  <td className="text-right py-2 px-2 font-medium">
                                    {formatCurrency(product.subtotal)}
                                  </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <Separator className="my-6" />

            {/* Price Calculation Section - Only visible to admin/accounting */}

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">价格计算</h3>
              
              <div className="flex items-center gap-2 mb-4">
                <Checkbox
                  id="price_includes_tax"
                  checked={priceIncludesTax}
                  onCheckedChange={handlePriceIncludesTaxChange}
                />
                <Label htmlFor="price_includes_tax" className="cursor-pointer">
                  价格含税
                </Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subtotal">小计</Label>
                  <Input
                    id="subtotal"
                    value={formatCurrency(formData.subtotal)}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div>
                  <Label htmlFor="tax_rate">税率 (%)</Label>
                  <Input
                    id="tax_rate"
                    type="number"
                    step="0.01"
                    value={(formData.tax_rate * 100).toFixed(0)}
                    onChange={(e) => handleTaxRateChange(e.target.value / 100)}
                    disabled={priceIncludesTax}
                  />
                </div>
                <div>
                  <Label htmlFor="tax_amount">税额</Label>
                  <Input
                    id="tax_amount"
                    value={formatCurrency(formData.tax_amount)}
                    readOnly
                    className="bg-muted"
                  />
                </div>
                <div>
                  <Label htmlFor="discount_amount">折扣金额</Label>
                  <Input
                    id="discount_amount"
                    type="number"
                    step="0.01"
                    value={formData.discount_amount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                  />
                </div>
              </div>

              <Separator className="my-4" />

              <div className="flex justify-between items-center text-lg font-semibold">
                <span>总计</span>
                <span className="text-2xl text-primary">{formatCurrency(formData.total_amount)}</span>
              </div>
              
              {/* Variance Display */}
              {hasVariance && (
                <div className={`mt-4 p-3 rounded-lg ${amountVariance > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">金额差异</span>
                    <span className={`font-semibold ${amountVariance > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                      {amountVariance > 0 ? '+' : ''}{formatCurrency(amountVariance)}
                    </span>
                  </div>
                </div>
              )}
            </div>



            <Separator className="my-6" />

            {/* Comment Section */}
            <div className="mb-6">
              <Label htmlFor="comment">备注</Label>
              <Textarea
                id="comment"
                rows={4}
                value={formData.comment}
                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="请输入备注信息..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mt-6">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={formData.purchase_orders.length === 0}
          >
            {mode === 'edit' ? '保存' : '创建'}
          </Button>
        </div>
      </div>

      {/* PO Selection Dialog */}
      <Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>选择采购单</DialogTitle>
            <DialogDescription>
              选择要添加到结算单的采购单。只能选择同一供应商的采购单。
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索采购单号或供应商..."
                value={poSearchTerm}
                onChange={(e) => setPoSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
            
            <div className="flex-1 overflow-auto">
              {poLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">采购单号</th>
                      <th className="px-4 py-2 text-left">关联SO</th>
                      <th className="px-4 py-2 text-left">供应商</th>
                      <th className="px-4 py-2 text-right">金额</th>
                      <th className="px-4 py-2 text-right">已入库</th>
                      <th className="px-4 py-2 text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {availablePOs
                      .filter(po => 
                        po.purchase_order_id?.toLowerCase().includes(poSearchTerm.toLowerCase()) ||
                        po.supplier_name?.toLowerCase().includes(poSearchTerm.toLowerCase())
                      )
                      .map((po) => (
                        <tr key={po._id} className="hover:bg-muted/50">
                          <td className="px-4 py-3 font-medium">{po.purchase_order_id}</td>
                          <td className="px-4 py-3">
                            {po.linked_so ? (
                              <Badge variant="outline" className="text-xs">
                                {po.linked_so.so_number}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">{po.supplier_name}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(po.total_amount)}</td>
                          <td className="px-4 py-3 text-right">{po.received_qty || 0}</td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              onClick={() => handleSelectPO(po)}
                            >
                              选择
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoDialogOpen(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog - LLM File Import Placeholder */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>从文件导入</DialogTitle>
            <DialogDescription>
              上传文件（Excel、PDF、图片），系统将自动提取数据填充表单
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <FileUp className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-2">拖拽文件到这里</p>
              <p className="text-sm text-muted-foreground mb-4">或</p>
              <Button variant="outline">选择文件</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              支持格式：Excel (.xlsx, .xls)、PDF、图片 (.png, .jpg)
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              取消
            </Button>
            <Button disabled>开始提取（开发中）</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO/SO Detail Drawer */}

      {/* PO/SO Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-[70vw] sm:max-w-[70vw] overflow-auto">
          <SheetHeader>
            <SheetTitle>
              {drawerType === 'po' ? '采购单详情' : '销售单详情'}
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6">
            {drawerLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : drawerData ? (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">单号:</span>
                    <span className="ml-2 font-medium">
                      {drawerType === 'po' ? drawerData.purchase_order_id : drawerData.sales_order_id}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">状态:</span>
                    <span className="ml-2">
                      <Badge variant="outline">{drawerData.status}</Badge>
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{drawerType === 'po' ? '供应商' : '客户'}:</span>
                    <span className="ml-2">
                      {drawerType === 'po' ? drawerData.supplier_name : drawerData.client_name}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">总金额:</span>
                    <span className="ml-2 font-medium">{formatCurrency(drawerData.total_amount)}</span>
                  </div>
                </div>
                
                <Separator />
                
                {/* Products */}
                <div>
                  <h4 className="font-medium mb-3">产品明细</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left">产品</th>
                        <th className="px-3 py-2 text-right">数量</th>
                        <th className="px-3 py-2 text-right">单价</th>
                        <th className="px-3 py-2 text-right">小计</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(drawerData.products || []).map((product, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">
                            <div className="font-medium">{product.name || product.product_name}</div>
                            <div className="text-xs text-muted-foreground">{product.product_code}</div>
                          </td>
                          <td className="px-3 py-2 text-right">{product.quantity}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(product.unit_price || product.price)}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(product.subtotal || product.quantity * (product.unit_price || product.price || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                无法加载订单详情
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Layout>
  );
};

export default SettlementFormPage;
